/**
 * Trigger metadata utilities for timeline display
 * Provides trigger categorization, naming, coloring, and icon selection
 */

import {
  PiChatLight,
  PiPhoneCallLight,
  PiClockLight,
  PiCalendarCheckLight,
  PiFunnelLight,
  PiUserPlusLight,
  PiArrowSquareOutLight,
  PiPlayLight,
  PiBellLight,
  PiCheckCircleLight,
  PiXCircleLight,
  PiArrowsClockwiseLight,
  PiTargetLight,
  PiEnvelopeLight,
  PiGearSixLight,
} from 'react-icons/pi';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';

/**
 * Trigger category definitions with their colors
 */
export enum TriggerCategory {
  MESSAGE = 'message',
  CALL = 'call',
  TASK = 'task',
  FUNNEL = 'funnel',
  CONTACT = 'contact',
  TIME = 'time',
  WORKFLOW = 'workflow',
  NOTIFICATION = 'notification',
  SYSTEM = 'system',
  UNKNOWN = 'unknown',
}

/**
 * Color mapping for each trigger category
 */
const CATEGORY_COLORS: Record<TriggerCategory, string> = {
  [TriggerCategory.MESSAGE]: 'text-blue-600',
  [TriggerCategory.CALL]: 'text-green-600',
  [TriggerCategory.TASK]: 'text-purple-600',
  [TriggerCategory.FUNNEL]: 'text-orange-600',
  [TriggerCategory.CONTACT]: 'text-pink-600',
  [TriggerCategory.TIME]: 'text-indigo-600',
  [TriggerCategory.WORKFLOW]: 'text-cyan-600',
  [TriggerCategory.NOTIFICATION]: 'text-yellow-600',
  [TriggerCategory.SYSTEM]: 'text-gray-600',
  [TriggerCategory.UNKNOWN]: 'text-gray-500',
};

/**
 * Badge variant mapping for each trigger category
 */
const CATEGORY_BADGE_VARIANTS: Record<
  TriggerCategory,
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral'
  | 'primary'
  | 'secondary'
> = {
  [TriggerCategory.MESSAGE]: 'info',
  [TriggerCategory.CALL]: 'success',
  [TriggerCategory.TASK]: 'secondary',
  [TriggerCategory.FUNNEL]: 'warning',
  [TriggerCategory.CONTACT]: 'primary',
  [TriggerCategory.TIME]: 'info',
  [TriggerCategory.WORKFLOW]: 'info',
  [TriggerCategory.NOTIFICATION]: 'warning',
  [TriggerCategory.SYSTEM]: 'neutral',
  [TriggerCategory.UNKNOWN]: 'neutral',
};

/**
 * Background colors for trigger info containers
 */
const CATEGORY_BG_COLORS: Record<TriggerCategory, string> = {
  [TriggerCategory.MESSAGE]: 'bg-blue-50 border-blue-100',
  [TriggerCategory.CALL]: 'bg-green-50 border-green-100',
  [TriggerCategory.TASK]: 'bg-purple-50 border-purple-100',
  [TriggerCategory.FUNNEL]: 'bg-orange-50 border-orange-100',
  [TriggerCategory.CONTACT]: 'bg-pink-50 border-pink-100',
  [TriggerCategory.TIME]: 'bg-indigo-50 border-indigo-100',
  [TriggerCategory.WORKFLOW]: 'bg-cyan-50 border-cyan-100',
  [TriggerCategory.NOTIFICATION]: 'bg-yellow-50 border-yellow-100',
  [TriggerCategory.SYSTEM]: 'bg-gray-50 border-gray-100',
  [TriggerCategory.UNKNOWN]: 'bg-gray-50 border-gray-100',
};

/**
 * Icon mapping for each trigger category
 */
const CATEGORY_ICONS: Record<TriggerCategory, React.ReactNode> = {
  [TriggerCategory.MESSAGE]: <PiChatLight className="w-4 h-4" />,
  [TriggerCategory.CALL]: <PiPhoneCallLight className="w-4 h-4" />,
  [TriggerCategory.TASK]: <PiCalendarCheckLight className="w-4 h-4" />,
  [TriggerCategory.FUNNEL]: <PiFunnelLight className="w-4 h-4" />,
  [TriggerCategory.CONTACT]: <PiUserPlusLight className="w-4 h-4" />,
  [TriggerCategory.TIME]: <PiClockLight className="w-4 h-4" />,
  [TriggerCategory.WORKFLOW]: <PiPlayLight className="w-4 h-4" />,
  [TriggerCategory.NOTIFICATION]: <PiBellLight className="w-4 h-4" />,
  [TriggerCategory.SYSTEM]: <PiGearSixLight className="w-4 h-4" />,
  [TriggerCategory.UNKNOWN]: <PiArrowSquareOutLight className="w-4 h-4" />,
};

/**
 * Action-specific icons that override category icons
 */
const ACTION_ICONS: Record<string, React.ReactNode> = {
  created: <PiUserPlusLight className="w-4 h-4" />,
  updated: <PiArrowsClockwiseLight className="w-4 h-4" />,
  completed: <PiCheckCircleLight className="w-4 h-4" />,
  cancelled: <PiXCircleLight className="w-4 h-4" />,
  changed: <PiArrowsClockwiseLight className="w-4 h-4" />,
  received: <PiEnvelopeLight className="w-4 h-4" />,
  scheduled: <PiClockLight className="w-4 h-4" />,
  achieved: <PiTargetLight className="w-4 h-4" />,
  missed: <PiXCircleLight className="w-4 h-4" />,
};

const TRIGGER_ACTIONS = [
  'created',
  'updated',
  'completed',
  'cancelled',
  'changed',
  'received',
  'scheduled',
  'achieved',
  'missed',
] as const;

/**
 * Trigger metadata including category, display name, color, icon, and styling
 */
export interface TriggerMetadata {
  category: TriggerCategory;
  displayName: string;
  color: string;
  icon: React.ReactNode;
  badgeVariant:
    | 'info'
    | 'success'
    | 'warning'
    | 'danger'
    | 'neutral'
    | 'primary'
    | 'secondary';
  bgColor: string;
}

/**
 * Determine trigger category from trigger ID
 */
const getCategoryFromTriggerId = (triggerId: string): TriggerCategory => {
  const lowerTriggerId = triggerId.toLowerCase();

  if (
    lowerTriggerId.includes('message') ||
    lowerTriggerId.includes('chat') ||
    lowerTriggerId.includes('whatsapp') ||
    lowerTriggerId.includes('sms')
  ) {
    return TriggerCategory.MESSAGE;
  }
  if (lowerTriggerId.includes('call') || lowerTriggerId.includes('phone')) {
    return TriggerCategory.CALL;
  }
  if (lowerTriggerId.includes('task')) {
    return TriggerCategory.TASK;
  }
  if (
    lowerTriggerId.includes('funnel') ||
    lowerTriggerId.includes('status') ||
    lowerTriggerId.includes('stage')
  ) {
    return TriggerCategory.FUNNEL;
  }
  if (lowerTriggerId.includes('contact')) {
    return TriggerCategory.CONTACT;
  }
  if (
    lowerTriggerId.includes('schedule') ||
    lowerTriggerId.includes('time') ||
    lowerTriggerId.includes('interval')
  ) {
    return TriggerCategory.TIME;
  }
  if (
    lowerTriggerId.includes('workflow') ||
    lowerTriggerId.includes('automation')
  ) {
    return TriggerCategory.WORKFLOW;
  }
  if (
    lowerTriggerId.includes('notification') ||
    lowerTriggerId.includes('alert')
  ) {
    return TriggerCategory.NOTIFICATION;
  }

  return TriggerCategory.UNKNOWN;
};

/**
 * Extract action type from trigger ID or resource action
 */
const getActionFromTrigger = (
  triggerId: string,
  resourceAction?: string
): string | null => {
  const sources = [triggerId.toLowerCase()];
  if (resourceAction) {
    sources.push(resourceAction.toLowerCase());
  }

  for (const source of sources) {
    const action = TRIGGER_ACTIONS.find((candidate) =>
      source.includes(candidate)
    );
    if (action) return action;
  }

  return null;
};

/**
 * Normalize and format trigger name for display
 * Removes common prefixes and formats to Title Case
 */
const normalizeTriggerId = (triggerId: string): string => {
  let normalized = triggerId;

  // Remove common prefixes
  normalized = normalized
    .replace(/^llamadapro\.trigger\./i, '')
    .replace(/^comos\.trigger\./i, '')
    .replace(/^trigger\./i, '');

  // Split by dots, dashes, or underscores
  const parts = normalized.split(/[._-]/);

  // Capitalize each part and join with spaces
  return parts
    .map((part) => {
      if (!part) return '';
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .filter(Boolean)
    .join(' ');
};

/**
 * Manual mapping for specific triggers (takes precedence over auto-generation)
 */
const TRIGGER_NAME_OVERRIDES: Record<string, string> = {
  'llamadapro.trigger.message-received': 'Mensaje Recibido',
  'llamadapro.trigger.whatsapp-message-received': 'Mensaje WhatsApp Recibido',
  'llamadapro.trigger.call-completed': 'Llamada Completada',
  'llamadapro.trigger.call-missed': 'Llamada Perdida',
  'llamadapro.trigger.task-completed': 'Tarea Completada',
  'llamadapro.trigger.task-cancelled': 'Tarea Cancelada',
  'llamadapro.trigger.funnel-status-changed': 'Cambio de Estado en Embudo',
  'llamadapro.trigger.contact-created': 'Contacto Creado',
  'llamadapro.trigger.contact-updated': 'Contacto Actualizado',
  'llamadapro.trigger.scheduled-time': 'Hora Programada',
  'llamadapro.trigger.workflow-started': 'Workflow Iniciado',
  'llamadapro.trigger.manual-trigger': 'Disparador Manual',
};

type TranslationKey = Parameters<I18nContextValue['t']>[0];

const TRIGGER_NAME_TRANSLATION_KEYS: Partial<Record<string, TranslationKey>> = {
  'llamadapro.trigger.message-received':
    'automations.aiAgents.metadata.triggers.messageReceived',
  'llamadapro.trigger.whatsapp-message-received':
    'automations.aiAgents.metadata.triggers.whatsappMessageReceived',
  'llamadapro.trigger.call-completed':
    'automations.aiAgents.metadata.triggers.callCompleted',
  'llamadapro.trigger.call-missed':
    'automations.aiAgents.metadata.triggers.callMissed',
  'llamadapro.trigger.task-completed':
    'automations.aiAgents.metadata.triggers.taskCompleted',
  'llamadapro.trigger.task-cancelled':
    'automations.aiAgents.metadata.triggers.taskCancelled',
  'llamadapro.trigger.funnel-status-changed':
    'automations.aiAgents.metadata.triggers.funnelStatusChanged',
  'llamadapro.trigger.contact-created':
    'automations.aiAgents.metadata.triggers.contactCreated',
  'llamadapro.trigger.contact-updated':
    'automations.aiAgents.metadata.triggers.contactUpdated',
  'llamadapro.trigger.scheduled-time':
    'automations.aiAgents.metadata.triggers.scheduledTime',
  'llamadapro.trigger.workflow-started':
    'automations.aiAgents.metadata.triggers.workflowStarted',
  'llamadapro.trigger.manual-trigger':
    'automations.aiAgents.metadata.triggers.manual',
};

/**
 * Get comprehensive metadata for a trigger
 */
export const getTriggerMetadata = (
  triggerId: string,
  resourceAction?: string,
  translate?: I18nContextValue['t']
): TriggerMetadata => {
  const category = getCategoryFromTriggerId(triggerId);
  const action = getActionFromTrigger(triggerId, resourceAction);

  // Get display name (use override if available, otherwise normalize)
  const translationKey = TRIGGER_NAME_TRANSLATION_KEYS[triggerId];
  const displayName =
    translationKey && translate
      ? translate(translationKey)
      : TRIGGER_NAME_OVERRIDES[triggerId] || normalizeTriggerId(triggerId);

  // Get color from category
  const color = CATEGORY_COLORS[category];

  // Get badge variant from category
  const badgeVariant = CATEGORY_BADGE_VARIANTS[category];

  // Get background color from category
  const bgColor = CATEGORY_BG_COLORS[category];

  // Get icon - prioritize action-based, fallback to category
  let icon: React.ReactNode;
  if (action && ACTION_ICONS[action]) {
    icon = ACTION_ICONS[action];
  } else {
    icon = CATEGORY_ICONS[category];
  }

  return {
    category,
    displayName,
    color,
    icon,
    badgeVariant,
    bgColor,
  };
};

/**
 * Get display name for a trigger (shorthand for common use case)
 */
export const getTriggerDisplayName = (triggerId: string): string => {
  return getTriggerMetadata(triggerId).displayName;
};

/**
 * Get color class for a trigger (shorthand for common use case)
 */
export const getTriggerColor = (triggerId: string): string => {
  return getTriggerMetadata(triggerId).color;
};

/**
 * Get icon for a trigger (shorthand for common use case)
 */
export const getTriggerIcon = (triggerId: string): React.ReactNode => {
  return getTriggerMetadata(triggerId).icon;
};

/**
 * Get background color classes for a trigger (shorthand for common use case)
 */
export const getTriggerBgColor = (triggerId: string): string => {
  return getTriggerMetadata(triggerId).bgColor;
};
